import step from "./step";
import { Paper, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as chartUtils from "chartUtils";
import * as numbers from "common/numbers";
import Component from "components/Component";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import Visible from "components/common/Visible";
import * as labels from "config.labels";
import {
  Bar,
  XAxis,
  Cell,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  ComposedChart,
  Tooltip,
  Line,
} from "recharts";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const getColor = (entry, theme) => {
  if (entry?.direction === "DOWN") {
    return theme.brand.red[100];
  }
  if (entry?.direction === "UP") {
    return theme.brand.green[100];
  }
  return theme.brand.chart.primary;
};

const fmtMoney = numbers.dpString(0);
const fmtPct1 = numbers.dpString(1, { suffix: "%" });
const fmtNum = numbers.dpString(2);

// The raw prior/current inputs behind each driver, from the backend `details` map (keyed by
// component key). Shows exactly the values that drove this calculation.
const DriverDetail = ({ detailKey, detail }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  if (!detail) {
    return null;
  }
  const lines = [];
  if (detailKey === "commission") {
    lines.push(
      `${translateLabel(
        labels.RATE_CHANGE_WATERFALL_CHART_TOOLTIP_PRIOR_COMMISSION,
        { default: "Prior commission" }
      )}: ${fmtPct1(detail.startCommission)}`
    );
    lines.push(
      `${translateLabel(
        labels.RATE_CHANGE_WATERFALL_CHART_TOOLTIP_CURRENT_COMMISSION,
        { default: "Current commission" }
      )}: ${fmtPct1(detail.endCommission)}`
    );
  } else if (detailKey === "structure") {
    if (detail.startDaysOnRisk != null || detail.endDaysOnRisk != null) {
      lines.push(
        `${translateLabel(
          labels.RATE_CHANGE_WATERFALL_CHART_TOOLTIP_DAYS_ON_RISK,
          { default: "Days on risk" }
        )}: ${detail.startDaysOnRisk ?? "?"} → ${detail.endDaysOnRisk ?? "?"}`
      );
    }
    if (detail.startProRataAdjustment != null) {
      lines.push(
        `${translateLabel(labels.RATE_CHANGE_WATERFALL_CHART_TOOLTIP_PRO_RATA, {
          default: "Pro-rata",
        })}: ${fmtNum(detail.startProRataAdjustment)} → ${fmtNum(
          detail.endProRataAdjustment
        )}`
      );
    }
  } else if (detailKey === "exposure") {
    if (detail.totalOverridePct != null) {
      lines.push(
        `${translateLabel(
          labels.RATE_CHANGE_WATERFALL_CHART_TOOLTIP_TOTAL_OVERRIDE,
          { default: "Total rate-impact override" }
        )}: ${fmtPct1(detail.totalOverridePct)}`
      );
    }
    (detail.metrics ?? []).forEach((metric) => {
      const change =
        metric.deltaPct != null ? ` (${fmtPct1(metric.deltaPct)})` : "";
      lines.push(
        `${metric.name}: ${fmtNum(metric.prior)} → ${fmtNum(
          metric.current
        )}${change}`
      );
    });
  } else if (detailKey === "uwJudgment") {
    const summarise = (judgments) =>
      (judgments ?? [])
        .map((judgment) => `${judgment.name ?? "—"} ${fmtPct1(judgment.value)}`)
        .join(", ");
    if (detail.startJudgments?.length) {
      lines.push(
        `${translateLabel(labels.RATE_CHANGE_WATERFALL_CHART_TOOLTIP_PRIOR, {
          default: "Prior",
        })}: ${summarise(detail.startJudgments)}`
      );
    }
    if (detail.endJudgments?.length) {
      lines.push(
        `${translateLabel(labels.RATE_CHANGE_WATERFALL_CHART_TOOLTIP_CURRENT, {
          default: "Current",
        })}: ${summarise(detail.endJudgments)}`
      );
    }
  }
  if (!lines.length) {
    return null;
  }
  return (
    <Stack sx={{ mt: 0.5 }}>
      {lines.map((line, index) => (
        <Typography key={index} variant={"caption"} color={"textSecondary"}>
          {line}
        </Typography>
      ))}
    </Stack>
  );
};

const RateChangeTooltip = ({ active, payload, details }) => {
  if (!active || !payload?.length) {
    return null;
  }
  const item = payload[0]?.payload;
  if (!item) {
    return null;
  }
  const isDelta = Array.isArray(item.value);
  const delta = isDelta ? item.value[1] - item.value[0] : null;
  const pct = delta != null && item.base ? (delta * 100) / item.base : null;
  return (
    <Paper elevation={3} sx={{ px: 1.25, py: 1, maxWidth: 300 }}>
      <Typography variant={"body2"} sx={{ fontWeight: 600 }}>
        {item.name}
      </Typography>
      {delta != null && (
        <Typography variant={"caption"} display={"block"}>
          {fmtMoney(delta)}
          {pct != null ? ` (${fmtPct1(pct)})` : ""}
        </Typography>
      )}
      <Visible
        byTag={"<RateChange>.waterfallChart.driverDetails"}
        defaultVisible={false}
      >
        <DriverDetail detailKey={item.key} detail={details?.[item.key]} />
      </Visible>
    </Paper>
  );
};

const transform = (c) => {
  if (c == null) {
    return c;
  }
  return (c.decomposition ?? []).map(({ key, name, value, base, delta }) =>
    delta == null
      ? {
          key,
          name: { start: "Expiring", end: "Premium" }[key] ?? name,
          value,
          base,
          line: value,
        }
      : {
          key,
          name,
          value: [value, value + delta],
          base,
          direction: delta >= 0 ? "UP" : "DOWN",
          line: value + delta,
        }
  );
};

const WaterfallChart = ({ activeLayerRateChange, calculate, isLoading }) => {
  const theme = useTheme();
  const {
    chartRef,
    tooltipPosition,
  } = chartUtils.useTooltipPositionerFixingBottomRightToChartTopRight();

  const transformedChartData = transform(activeLayerRateChange?.chartData);

  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  return (
    <Component
      title={translateLabel(labels.RATE_CHANGE_WATERFALL_CHART_TITLE, {
        default: "Rate Change",
      })}
      loading={isLoading}
      options={
        <Disabled ifReadOnly>
          <Button onClick={calculate} isLoading={isLoading}>
            {translateLabel(
              labels.RATE_CHANGE_WATERFALL_CHART_CALCULATE_BUTTON,
              {
                default: "Calculate",
              }
            )}
          </Button>
        </Disabled>
      }
    >
      <ResponsiveContainer width={"100%"} height={400}>
        <ComposedChart
          data={transformedChartData}
          barCategoryGap={0}
          margin={{
            top: 0,
            right: 50,
            left: 0,
            bottom: 60,
          }}
          ref={chartRef}
        >
          <CartesianGrid strokeDasharray={"3 3"} />
          <XAxis dataKey={"name"} angle={-45} textAnchor={"end"} />
          <YAxis tickFormatter={chartUtils.tickFormatter} />
          <Tooltip
            content={
              <RateChangeTooltip
                details={activeLayerRateChange?.chartData?.details}
              />
            }
            position={tooltipPosition}
            isAnimationActive={false}
          />
          <Bar dataKey={"value"}>
            {transformedChartData?.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getColor(entry, theme)} />
            ))}
          </Bar>
          <Line
            dataKey={"line"}
            type={step}
            dot={false}
            stroke={theme.brand.black}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Component>
  );
};

export default WaterfallChart;
